/**
 * Export dialog (contracts-plan.md §8.6, WP-54; §11 tab 4). Both paths as
 * radios (§8.6 wording), an optional "compare two versions" pair (R34), and
 * the "unverified in Excel" caution.
 *
 * THE excel_check RULE (this WP's brief, [OWNER]): Appendix A exposes no
 * route for the recorded exports (`listExports` exists on WP-54's service
 * with no route serving it) — so the badge shows "unverified in Excel"
 * ALWAYS until an export response comes back with `excelCheck: 'passed'`,
 * at which point that path clears for THIS SESSION only (state lives in
 * WorkspaceTab.tsx, passed down as `excelCheckPassed`).
 *
 * The bytes are not on the export response: downloading calls
 * `GET /api/contracts/versions/:versionId/download` (WP-32, mounted) and
 * opens the presigned url with `window.open(url, '_blank', 'noopener')` —
 * never axios, never a hand-built base URL.
 *
 * `POST /workspaces/:id/export`'s router FILE exists but is NOT MOUNTED yet
 * (routes/contracts.js has no `createExportsRouter` line — the MOUNT step
 * owes it) — it 404s in production today. `onError` surfaces that loudly
 * (G13): a toast in the parent plus this dialog's own inline
 * `ws-export-error`.
 */
import { useEffect, useState } from 'react';
import { Modal, FilterSelect, primaryBtnCls, secondaryBtnCls } from '../../../ui';
import {
  downloadVersion,
  exportWorkspace,
  listNegotiationVersions,
  describeWorkspaceApiError,
  type ContractsVersion,
  type ExcelCheckStatus,
  type ExportPathMode,
  type ExportWorkspaceResult,
} from '../../api/workspace';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  negotiationId: string;
  /** Session-scoped: cleared on this session the moment excelCheck comes back 'passed' for that path (see this file's header). */
  excelCheckPassed: Record<ExportPathMode, boolean>;
  onExported: (pathMode: ExportPathMode, result: ExportWorkspaceResult) => void;
  onError: (message: string) => void;
}

const PATH_LABEL: Record<ExportPathMode, string> = {
  edit_in_place: 'Edit in place — rewrite the sponsor’s own file',
  own_version: 'WCR’s own version (§8.7 workbook)',
};

const EXCEL_CHECK_LABEL: Record<ExcelCheckStatus, string> = {
  pending: 'unverified in Excel',
  passed: 'verified in Excel',
  failed: 'failed the Excel check',
  not_required: 'not required',
};

export function ExportDialog({ isOpen, onClose, workspaceId, negotiationId, excelCheckPassed, onExported, onError }: Props) {
  const [pathMode, setPathMode] = useState<ExportPathMode | ''>('');
  const [compare, setCompare] = useState(false);
  const [versions, setVersions] = useState<ContractsVersion[]>([]);
  const [versionA, setVersionA] = useState('');
  const [versionB, setVersionB] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExportWorkspaceResult | null>(null);

  useEffect(() => {
    if (!compare || !isOpen) return;
    listNegotiationVersions(negotiationId)
      .then(setVersions)
      .catch(() => setVersions([]));
  }, [compare, isOpen, negotiationId]);

  if (!isOpen) return null;

  const path = `/api/contracts/workspaces/${workspaceId}/export`;

  async function handleExport() {
    if (!pathMode) return;
    setSubmitting(true);
    setError(null);
    try {
      const input: { pathMode: ExportPathMode; versionIds?: [string, string] } = { pathMode };
      if (compare && versionA && versionB) input.versionIds = [versionA, versionB];
      const exported = await exportWorkspace(workspaceId, input);
      setResult(exported);
      onExported(pathMode, exported);
    } catch (err) {
      const message = describeWorkspaceApiError('POST', path, err);
      setError(message);
      onError(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDownload() {
    if (!result) return;
    setDownloading(true);
    setError(null);
    try {
      const download = await downloadVersion(result.versionId);
      window.open(download.url, '_blank', 'noopener');
    } catch (err) {
      setError(describeWorkspaceApiError('GET', `/api/contracts/versions/${result.versionId}/download`, err));
    } finally {
      setDownloading(false);
    }
  }

  function close() {
    setResult(null);
    setError(null);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={close} title="Export">
      <div data-testid="ws-export-dialog" className="space-y-3">
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">Path (§8.6)</legend>
          {(['edit_in_place', 'own_version'] as ExportPathMode[]).map((pm) => (
            <label key={pm} data-testid={`ws-export-path-${pm}`} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="ws-export-path"
                value={pm}
                checked={pathMode === pm}
                onChange={() => setPathMode(pm)}
              />
              <span>
                {PATH_LABEL[pm]}
                {!excelCheckPassed[pm] && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    unverified in Excel
                  </span>
                )}
              </span>
            </label>
          ))}
        </fieldset>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
          Compare two versions instead of the accepted changes (R34)
        </label>
        {compare && (
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Version A"
              auto
              value={versionA}
              onChange={setVersionA}
              options={[{ value: '', label: 'Choose…' }, ...versions.map((v) => ({ value: v.id, label: `#${v.version_no}` }))]}
            />
            <FilterSelect
              label="Version B"
              auto
              value={versionB}
              onChange={setVersionB}
              options={[{ value: '', label: 'Choose…' }, ...versions.map((v) => ({ value: v.id, label: `#${v.version_no}` }))]}
            />
          </div>
        )}

        {error && (
          <p data-testid="ws-export-error" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        {!result ? (
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className={secondaryBtnCls} onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              data-testid="ws-export-submit"
              className={primaryBtnCls}
              disabled={!pathMode || submitting}
              onClick={handleExport}
            >
              Export
            </button>
          </div>
        ) : (
          <div data-testid="ws-export-result" className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-100">
              Exported as version #{result.version?.version_no ?? '—'} ({result.filename}) —{' '}
              <span
                className={
                  result.excelCheck === 'passed'
                    ? 'text-green-700 dark:text-green-400'
                    : 'font-medium text-amber-700 dark:text-amber-400'
                }
              >
                {EXCEL_CHECK_LABEL[result.excelCheck]}
              </span>
              . A person still checks the file in Excel desktop before it goes to a sponsor (§14).
            </p>
            {result.duplicateWarning && (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                A version with these exact bytes (same sha256) is already on record for this negotiation — it was
                still filed as a new sent version (§4.5, never a refusal).
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button type="button" className={secondaryBtnCls} onClick={close}>
                Close
              </button>
              <button
                type="button"
                data-testid="ws-export-download"
                className={primaryBtnCls}
                disabled={downloading}
                onClick={handleDownload}
              >
                Download
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ExportDialog;
